import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import ScheduledReportsList from '@/components/ScheduledReports/ScheduledReportsList'
import { UserPermission } from '@/enums/scenario-builder.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import { IScheduledReportsDTO2 } from '@/interfaces/scenarioAnalysisReportComponents.interface'
import { deleteScheduledReport2, getScheduledReports2 } from '@/services/axiomReport.service'
import { userHasPermission } from '@/services/scenarioBuilder.service'
import { toggleRunReportTemplateModalOpened } from '@/states/scenarioBuilder.slice'
import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

export const ReportSchedulesAccordion: React.FC = () => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = React.useState<boolean>(false)
  const handleAccordionChange = () => {
    setExpanded(!expanded)
  }
  const hasNewModifiedValues = useAppSelector((s) => s.scenarioBuilder.hasNewModifiedValues)
  const userInfo = useAppSelector((s) => s.scenarioBuilder.userInfo)

  const hasRunPermission = userHasPermission(userInfo, UserPermission.RUN_SCENARIO)

  const hasSchedulePermission = userHasPermission(userInfo, UserPermission.SCHEDULE_SCENARIO)

  const isRunReportTemplateModalOpened = useAppSelector((s) => s.scenarioBuilder.isRunReportTemplateModalOpened)
  const wasRunReportTemplateModalOpened = useRef(false)

  const [scheduledReports, setScheduledReports] = useState<IScheduledReportsDTO2[]>([])
  const [scheduledReportsLoading, setScheduledReportsLoading] = useState<boolean>(true)

  const fetchScheduledReports = async () => {
    try {
      setScheduledReportsLoading(true)
      const data = await getScheduledReports2()
      setScheduledReports(data ?? [])
    } catch (error) {
      console.warn(error)
    } finally {
      setScheduledReportsLoading(false)
    }
  }

  useEffect(() => {
    fetchScheduledReports()
  }, [])

  // The Create Report modal closes on both successful submit and cancel; either
  // way, refresh the table so a newly scheduled report shows up right away.
  useEffect(() => {
    if (wasRunReportTemplateModalOpened.current && !isRunReportTemplateModalOpened) {
      fetchScheduledReports()
    }
    wasRunReportTemplateModalOpened.current = isRunReportTemplateModalOpened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunReportTemplateModalOpened])

  const handleDeleteScheduledReport = async (scheduleId: number) => {
    try {
      await deleteScheduledReport2(scheduleId)
      await fetchScheduledReports()
    } catch (error) {
      console.error('Failed to delete scheduled report:', error)
      toast.error('Failed to delete scheduled report. Please try again later.')
    }
  }

  return (
    <Accordion expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box
          display='grid'
          gridTemplateColumns='1fr auto'
          width='100%'
          alignItems='center'
          padding='1rem 0'
          gap={4}
          boxSizing='border-box'
        >
          <Typography variant='h3'>Report Schedules</Typography>

          {(hasRunPermission || hasSchedulePermission) && (
            <Tooltip placement='top' arrow title={hasNewModifiedValues ? 'Template changes are pending' : ''}>
              <span>
                <Button
                  variant='contained'
                  disabled={hasNewModifiedValues}
                  color='secondary'
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation()
                    dispatch(toggleRunReportTemplateModalOpened(true))
                  }}
                >
                  Create Report
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ height: '400px', width: '100%' }}>
          {scheduledReportsLoading && <LoadingCard />}
          {!scheduledReportsLoading && !scheduledReports.length && <NoDataToDisplayCard />}
          {!scheduledReportsLoading && scheduledReports.length > 0 && (
            <ScheduledReportsList rowData={scheduledReports} onDelete={handleDeleteScheduledReport} />
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
